// Stage 201-S: pure builder for a joint-reframe engram.
//
// A joint-reframe records that BOTH parties reframed in the same turn.
// Stage 201-S first pass: a same-turn co-occurrence join (a self-laugh
// AND a witness-reframe landed together) → cooccurrenceWindow:'same-turn'.
//
// Stage 202-J deep form: when the two-pass shared-interpretive-ground read
// confirms the shared model relocated (both parties), the builder is given
// a `sharedGround` payload → cooccurrenceWindow:'shared-ground' +
// detector:'shared-ground-v1'.
//
// The engram carries the witnessReframe payload regardless (so the
// validator's non-self branch is satisfied) and threads the Stage-202-W
// detector / semantic fields through.

import type { LaughterEngramInput, WitnessReframeCue } from './types';
import type { WitnessSemantic } from './buildWitnessReframeEngram';

export interface JointSharedGround {
  before: string;
  after: string;
  confidence: 'high' | 'medium' | 'low';
}

export function buildJointReframeEngram(args: {
  conversationId: string;
  turnId: string;
  userMessage: string;
  normalisedText: string;
  cues: WitnessReframeCue[];
  selfFiredBiteIndices: number[];
  firedAt: string;
  detector: 'lexical-heuristic-v1' | 'semantic-v1';
  semantic?: WitnessSemantic;
  /** Stage 202-J: present when the deep shared-ground read confirmed. */
  sharedGround?: JointSharedGround;
}): LaughterEngramInput {
  return {
    reframeSubject: 'joint',
    userMessage: args.userMessage,
    personaResponse: '',
    turnId: args.turnId,
    conversationId: args.conversationId,
    firedAt: args.firedAt,
    witnessReframe: {
      userMessage: args.userMessage,
      normalisedText: args.normalisedText,
      cues: args.cues,
      detector: args.detector,
      ...(args.semantic ? { semantic: args.semantic } : {}),
    },
    jointReframe: {
      selfFiredBiteIndices: args.selfFiredBiteIndices,
      cooccurrenceWindow: args.sharedGround ? 'shared-ground' : 'same-turn',
      ...(args.sharedGround
        ? {
            sharedGround: {
              ...args.sharedGround,
              detector: 'shared-ground-v1' as const,
            },
          }
        : {}),
    },
  };
}
